// src/components/document/DocumentViewer/viewers/OfficeViewer.tsx

import { RefObject, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Document, DocumentPreviewEvent } from "@/api/rest/models/document";
import { downloadDocument2, getDocumentUrl2, logDocumentPreviewEvent } from "@/api/rest/services/documentService";
import Button from "@/components/common/Button";

import { ViewerProps } from "../types";

// Office Online renders cross-origin and reports nothing back, so these events are our only view of blank previews:
// OPENED with no FRAME_LOADED, or a FALLBACK_DOWNLOAD / DOWNLOAD soon after OPENED.
const logOfficePreviewEvent = (documentId: number, event: DocumentPreviewEvent) =>
  logDocumentPreviewEvent(documentId, "OFFICE", event);

/** Logs OPENED now and CLOSED on cleanup; shaped as a useEffect body. */
export const trackOfficePreview = (documentId: number) => {
  logOfficePreviewEvent(documentId, "OPENED");
  return () => logOfficePreviewEvent(documentId, "CLOSED");
};

export const downloadFromOfficePreview = (document: Document) => {
  logOfficePreviewEvent(document.id, "FALLBACK_DOWNLOAD");
  return downloadDocument2(getDocumentUrl2(document), document.originalFilename);
};

// Sooner would flash the hint on healthy documents: most render within 10s, a 22 MB docx took up to 20s.
const BLANK_PREVIEW_MS = 10_000;
const RENDER_POLL_MS = 500;

/**
 * Tells a rendered document from outside the cross-origin frame. Frame counts are all browsers expose:
 *
 *   embed.aspx                     our iframe
 *   └─ wordviewerframe / xlembed   Microsoft's viewer app, contentWindow[0]
 *      └─ helper frames            opened once the document renders
 *
 * Error pages have no viewer app. A stuck splash or password prompt has no helper frames.
 */
export const hasOfficeViewerRendered = (frame: HTMLIFrameElement | null) => {
  const embed = frame?.contentWindow;
  // Reading past the last frame of a cross-origin window throws, so length comes first.
  if (!embed?.length) {
    return false;
  }
  return embed[0].length > 0;
};

/** True once the preview has gone BLANK_PREVIEW_MS without rendering. A late render clears it. */
const useBlankOfficePreview = (frameRef: RefObject<HTMLIFrameElement | null>, documentId: number) => {
  const [blank, setBlank] = useState(false);

  useEffect(() => {
    const openedAt = Date.now();
    const poll = setInterval(() => {
      const rendered = hasOfficeViewerRendered(frameRef.current);
      setBlank(!rendered && Date.now() - openedAt >= BLANK_PREVIEW_MS);
      if (rendered) {
        clearInterval(poll);
      }
    }, RENDER_POLL_MS);

    return () => {
      clearInterval(poll);
      setBlank(false);
    };
  }, [frameRef, documentId]);

  return blank;
};

export const OfficeViewer: React.FC<ViewerProps> = ({ document, onSuccess, onError, className }) => {
  const { t } = useTranslation();
  const frameRef = useRef<HTMLIFrameElement>(null);
  const blank = useBlankOfficePreview(frameRef, document.id);

  useEffect(() => trackOfficePreview(document.id), [document.id]);

  // The Office Online frame is cross-origin: onError never fires and onLoad fires before it renders.
  // hasOfficeViewerRendered is the real signal: the hint shows only when the viewer stays blank.
  return (
    <div className={className}>
      <div className="flex flex-col h-full">
        {blank && (
          <div className="flex flex-none items-center justify-end gap-2 px-4 py-2 text-sm text-gray-600">
            <span>{t("files.officePreviewHint")}</span>
            <Button variant="outline" size="sm" onClick={() => downloadFromOfficePreview(document)}>
              {t("files.download")}
            </Button>
          </div>
        )}
        <iframe
          ref={frameRef}
          src={`https://view.officeapps.live.com/op/embed.aspx?src=${getDocumentUrl2(document, true)}`}
          className="flex-1 min-h-0"
          onLoad={() => {
            logOfficePreviewEvent(document.id, "FRAME_LOADED");
            onSuccess();
          }}
          style={{ width: "100%", border: "none" }}
          onError={() => onError(new Error("Failed to load Office document"))}
        />
      </div>
    </div>
  );
};

export const isOfficeDocument = (type: string): boolean => {
  return ["doc", "docx", "xls", "xlsx"].includes(type);
};
